import {
	FaBuilding,
	FaPalette,
	FaEnvelope,
	FaBoxes,
	FaListUl,
	FaLanguage,
	FaColumns,
	FaThLarge,
	FaFileInvoiceDollar,
	FaUsers,
	FaGoogle,
	FaBroom,
	FaHistory,
	FaFileContract,
	FaTags,
	FaHeading,
	FaTable,
	FaWpforms,
	FaRegHandPointer,
	FaCommentDots,
	FaCogs,
	FaFileSignature,
} from 'react-icons/fa';
import { isContractTemplatesEnabled, isEsignatureEnabled, isFacturacionEnabled } from '../../utils/featureFlags';

/**
 * Settings navigation registry. Single source of truth for the sidebar, the
 * cards landing page and the route tree (settingsRoutes.jsx maps paths to
 * page elements; every routed path here must have a matching <Route>).
 *
 * item: {
 *   key, labelKey, path, icon,
 *   enabled?: () => boolean   // feature gate, item hidden when false
 *   adminOnly?: boolean       // only admin/super_admin roles
 *   danger?: boolean          // destructive tool styling
 * }
 * Labels resolve through the terminology layer at render time via t(labelKey).
 */
export const SETTINGS_SECTIONS = [
	{
		key: 'empresa',
		labelKey: 'settings.group.company',
		descriptionKey: 'settings.groupDesc.company',
		icon: FaBuilding,
		items: [
			{ key: 'info', labelKey: 'settings.item.companyInfo', path: '/AdminScreen/Config/empresa/info', icon: FaBuilding },
			{ key: 'colores', labelKey: 'settings.item.colors', path: '/AdminScreen/Config/empresa/colores', icon: FaPalette },
			{ key: 'emails', labelKey: 'settings.item.emails', path: '/AdminScreen/Config/empresa/emails', icon: FaEnvelope },
		],
	},
	{
		key: 'negocio',
		labelKey: 'settings.group.business',
		descriptionKey: 'settings.groupDesc.business',
		icon: FaBoxes,
		items: [
			{ key: 'preset', labelKey: 'settings.item.preset', path: '/AdminScreen/Config/negocio/preset', icon: FaBoxes },
			{ key: 'conceptosDeuda', labelKey: 'title.debtConceptConfig', path: '/AdminScreen/Config/negocio/conceptos-deuda', icon: FaListUl },
			{ key: 'conceptosPasividad', labelKey: 'title.creditConceptConfig', path: '/AdminScreen/Config/negocio/conceptos-pasividad', icon: FaListUl },
			{ key: 'tabs', labelKey: 'title.tabConfig', path: '/AdminScreen/Config/negocio/tabs', icon: FaColumns },
			{ key: 'dashboard', labelKey: 'settings.item.dashboard', path: '/AdminScreen/Config/negocio/dashboard', icon: FaThLarge },
		],
	},
	{
		key: 'terminologia',
		labelKey: 'settings.group.terminology',
		descriptionKey: 'settings.groupDesc.terminology',
		icon: FaLanguage,
		items: [
			{ key: 'conceptos', labelKey: 'settings.item.termEntities', path: '/AdminScreen/Config/terminologia/conceptos', icon: FaTags },
			{ key: 'titulos', labelKey: 'settings.item.termTitles', path: '/AdminScreen/Config/terminologia/titulos', icon: FaHeading },
			{ key: 'campos', labelKey: 'settings.item.termFields', path: '/AdminScreen/Config/terminologia/campos', icon: FaTable },
			{ key: 'formularios', labelKey: 'settings.item.termForms', path: '/AdminScreen/Config/terminologia/formularios', icon: FaWpforms },
			{ key: 'botones', labelKey: 'settings.item.termActions', path: '/AdminScreen/Config/terminologia/botones', icon: FaRegHandPointer },
			{ key: 'mensajes', labelKey: 'settings.item.termMessages', path: '/AdminScreen/Config/terminologia/mensajes', icon: FaCommentDots },
			{ key: 'panel', labelKey: 'settings.item.termAdmin', path: '/AdminScreen/Config/terminologia/panel', icon: FaCogs },
		],
	},
	{
		key: 'contabilidad',
		labelKey: 'settings.group.accounting',
		descriptionKey: 'settings.groupDesc.accounting',
		icon: FaFileInvoiceDollar,
		items: [
			{ key: 'info', labelKey: 'title.accountingInfo', path: '/AdminScreen/Config/contabilidad/info', icon: FaFileInvoiceDollar },
		],
	},
	{
		key: 'usuarios',
		labelKey: 'settings.group.users',
		descriptionKey: 'settings.groupDesc.users',
		icon: FaUsers,
		items: [
			{ key: 'lista', labelKey: 'settings.item.users', path: '/AdminScreen/Config/usuarios', icon: FaUsers, adminOnly: true },
		],
	},
	{
		key: 'integraciones',
		labelKey: 'settings.group.integrations',
		descriptionKey: 'settings.groupDesc.integrations',
		icon: FaGoogle,
		items: [
			{ key: 'calendar', labelKey: 'settings.item.googleCalendar', path: '/AdminScreen/Config/integraciones/calendar', icon: FaGoogle },
			{ key: 'firma', labelKey: 'settings.item.esignature', path: '/AdminScreen/Config/integraciones/firma', icon: FaFileSignature, enabled: isEsignatureEnabled, adminOnly: true },
			{ key: 'facturacion', labelKey: 'settings.item.facturacion', path: '/AdminScreen/Config/integraciones/facturacion', icon: FaFileInvoiceDollar, enabled: isFacturacionEnabled, adminOnly: true },
		],
	},
	{
		key: 'herramientas',
		labelKey: 'settings.group.tools',
		descriptionKey: 'settings.groupDesc.tools',
		icon: FaBroom,
		items: [
			{ key: 'limpieza', labelKey: 'settings.item.duplicateCleanup', path: '/AdminScreen/Config/herramientas/limpieza-duplicados', icon: FaBroom, danger: true },
			{ key: 'auditoria', labelKey: 'settings.item.auditTracking', path: '/AdminScreen/Config/herramientas/auditoria', icon: FaHistory },
			{ key: 'plantillas', labelKey: 'title.contractTemplates', path: '/AdminScreen/Config/herramientas/plantillas', icon: FaFileContract, enabled: isContractTemplatesEnabled },
		],
	},
];

const ADMIN_ROLES = ['admin', 'super_admin'];

/** Apply enabled()/adminOnly gating and drop empty groups. Runs BEFORE search. */
export const getVisibleSections = (role) =>
	SETTINGS_SECTIONS.map((section) => ({
		...section,
		items: section.items.filter(
			(item) => (!item.enabled || item.enabled()) && (!item.adminOnly || ADMIN_ROLES.includes(role))
		),
	})).filter((section) => section.items.length > 0);

/** Accent-insensitive normalization ("configuracion" matches "Configuración"). */
export const normalizeSearch = (s) =>
	String(s)
		.toLowerCase()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '');
